/**
 * Board payload normalization — the compatibility seam between this frontend
 * and whatever proxy revision happens to be answering it.
 *
 * The frontend (Firebase Hosting) and the proxy (Cloud Run) deploy separately,
 * and local dev runs against the PRODUCTION proxy by default. So a board
 * payload that predates a section this UI reads is a NORMAL state, not a bug —
 * and it must never take the whole board down.
 *
 * It did exactly that once: the 🏁 Final Rounds tab shipped reading
 * `role.finalRounds.candidates`, the deployed proxy had no `finalRounds` yet,
 * and the board rendered "Could not load the board: Cannot read properties of
 * undefined (reading 'candidates')" — every tab gone, not just the new one.
 *
 * Rule for any future section: add it here with an empty default, so an older
 * proxy costs you that one tab and nothing else.
 */
import type { BoardResponse, FinalRoundsSection, RoleBoard } from '../types/taActionBoard';

/** A role's 🏁 section as it looks with no template and nothing waiting. */
export const EMPTY_FINAL_ROUNDS: FinalRoundsSection = {
  candidates: [],
  config: null,
  chains: [],
  chainError: null,
};

/**
 * Fill in sections the answering proxy did not send.
 *
 * `finalRoundsUnavailable` is set when ANY role lacked the section — the tab
 * then says so instead of rendering an empty list that looks like "no work to
 * do". It is client-side only: the next Refresh against an updated proxy
 * clears it.
 */
export function normalizeBoardPayload(board: BoardResponse): BoardResponse {
  const roles = board?.roles;
  if (!Array.isArray(roles)) return board;

  let finalRoundsMissing = false;
  const normalized: RoleBoard[] = roles.map((role) => {
    // `cancelled` is newer than `finalRounds`, and this frontend talks to the
    // PROD proxy in local dev — so an answering proxy without it must degrade
    // to an empty list, not to a crash in the Feedback tab.
    const withCancelled = Array.isArray(role?.cancelled) ? role : { ...role, cancelled: [] };
    if (withCancelled?.finalRounds) return withCancelled;
    finalRoundsMissing = true;
    return { ...withCancelled, finalRounds: EMPTY_FINAL_ROUNDS };
  });

  return {
    ...board,
    roles: normalized,
    ...(finalRoundsMissing ? { finalRoundsUnavailable: true } : {}),
    // Absent means an older proxy that has no lock — the safe reading is
    // "unlocked", because inventing a lock the server will not enforce would
    // disable working buttons for no reason.
    pilot: board.pilot ?? {
      enabled: false,
      candidateActionsAllowed: true,
      message: null,
      featureGate: false,
      liveFeatures: [],
    },
  };
}
